import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Sparkles, Package, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useCart } from '../context/CartContext';

const DIYComponents = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart, toggleCart } = useCart();

  useEffect(() => {
    fetchDIYProducts();
  }, []);

  const fetchDIYProducts = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/products/diy`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching DIY products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    toggleCart(); // Open cart sidebar to show item added
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-primary flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Sparkles className="w-12 h-12 text-gold" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-primary">
      {/* Header */}
      <motion.header
        className="sticky top-0 z-40 bg-dark-primary/80 backdrop-blur-lg border-b border-gold/10"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gold hover:text-gold-dark transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <Package className="w-6 h-6 text-gold" />
              <h1 className="text-2xl font-bold text-white">DIY Components</h1>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="py-20 border-b border-gold/10">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="bg-gold/10 text-gold border-gold/30 mb-6 text-sm px-4 py-1">
              Premium Components
            </Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Build Your Own
              <span className="text-gold"> Masterpiece</span>
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed">
              High-quality components used in our rotating windmill. Perfect for DIY enthusiasts, 
              makers, and those who love to create their own mechanical art.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="bg-dark-secondary border-gold/10 hover:border-gold/30 transition-all duration-500 h-full group overflow-hidden">
                  <CardContent className="p-0">
                    {/* Product Image */}
                    <div className="relative aspect-square overflow-hidden bg-dark-primary">
                      <motion.img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.4 }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-primary via-transparent to-transparent opacity-60" />
                    </div>

                    {/* Product Details */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-3 line-clamp-2 min-h-[3.5rem]">
                        {product.name}
                      </h3>
                      <p className="text-gray-400 text-sm mb-4 line-clamp-3 min-h-[4rem]">
                        {product.description}
                      </p>

                      {/* Price and Add to Cart */}
                      <div className="flex items-center justify-between pt-4 border-t border-gold/10">
                        <div>
                          <p className="text-2xl font-bold text-gold">
                            ₹{product.price.toLocaleString('en-IN')}
                          </p>
                          <p className="text-xs text-gray-500">Inclusive of all taxes</p>
                        </div>
                        <Button
                          onClick={() => handleAddToCart(product)}
                          className="bg-gold hover:bg-gold-dark text-dark-primary font-semibold"
                        >
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Add
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Shipping Info Banner */}
      <section className="py-16 bg-dark-secondary border-t border-gold/10">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-white mb-4">Shipping Information</h3>
            <div className="grid md:grid-cols-2 gap-6 text-left">
              <div className="bg-dark-primary p-6 rounded-lg border border-gold/10">
                <h4 className="text-gold font-semibold mb-2">🇮🇳 India</h4>
                <p className="text-gray-400">Free shipping on all orders, any quantity</p>
              </div>
              <div className="bg-dark-primary p-6 rounded-lg border border-gold/10">
                <h4 className="text-gold font-semibold mb-2">🌍 International</h4>
                <p className="text-gray-400">₹1,000 flat rate per 5 items (6-10 items: ₹2,000, etc.)</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DIYComponents;
